/**
 * Created by Alan Yang on 4/05/2019.
 * Description:
 * Flat File management component
 *
 * ------ maintenance history ------
 */
import { Component, Input, OnInit } from '@angular/core';
import { of as observableOf } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { AlertWindowService } from '../../widgets/alert-window/alert-window.service';
import { AppConfig } from '../../tamalelibs/models/app-config.model';
import { TamUploadConfig, TamUploadUIFile } from '../../widgets/tam-upload/tam-upload.model';
import { FlatFileConfig } from './upload-flat-file-dialog.view-model';

import { UploadFlatFileDialogService, FlatFileActionService } from './upload-flat-file-dialog.service';
import { ToastService } from '../../widgets/toast/toast.service';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { DataProviderService } from '../../tamalelibs/services/dataprovider.service';
const Papaparse = require('papaparse');

@Component({
    selector: 'tam-upload-flat-file-dialog',
    templateUrl: './upload-flat-file-dialog.component.html',
    styleUrls: ['./upload-flat-file-dialog.component.scss']
})
export class UploadFlatFileDialogComponent implements OnInit {
    @Input() config: FlatFileConfig;

    btnDisabled = false; // if there is no file on dropzone, disable save button.
    dialogOpened = false;
    uploadConfig: TamUploadConfig;
    uploadEditing = 0;

    private _dropedFile: any; // the accepted file in dropzone.
    private _selectedRowData: any; // the selected one row data of flat file list.

    constructor(
        private _alertWindow: AlertWindowService,
        private _flatFileDataProviderService: DataProviderService,
        private _flatFileActionService: FlatFileActionService,
        private _toast: ToastService,
        private _uploadFlatFileDialogService: UploadFlatFileDialogService
    ) { }

    ngOnInit() {
        this._uploadFlatFileDialogService.dialogOpen$.subscribe((selectedRow) => {
            this._selectedRowData = selectedRow.data;
            this._initUpload(selectedRow.data);
            this.dialogOpened = true;
        });
    }

    closeDialog() {
        this.uploadConfig.files = [];
        this._dropedFile = null;
        this.dialogOpened = false;
    }

    /**
     * Upload file on save button clicked.
     */
    onSaveClicked() {
        this._parseCSVData(this._selectedRowData, this._dropedFile, this._flatFileDataProviderService,
            this._flatFileActionService, this._toast, this._sendRequest);

        this.closeDialog();
    }

    onCancelClicked() {
        if (!this.btnDisabled || (this.btnDisabled && this.uploadConfig.files.length === 0)) {
            // can use the save button, it stands the user has changed dialog,need to alert
            // cannot use the save button, it includes two situation: one is no flat file, and another one is no modified the file
            const subscription = this._alertWindow.warn('You have unsaved changes',
                ['Are you sure you want to discard all your changes?'])
                .subscribe((result: boolean) => {
                    if (result) {
                        this.closeDialog();
                    }
                    subscription.unsubscribe();
                });
        } else {
            this.closeDialog();
        }
    }

    showDialog() {
        this.dialogOpened = true;
    }

    private _initUpload(selectedRowData) {
        this.uploadEditing = 0; // represents number of elements in upload widget being editing
        this.uploadConfig = {
            files: [],
            threadFileNameCount: new Map<string, number>(),
            url: AppConfig.dataproviderEndPoint, // the endpoint for flat file.
            acceptedFiles: '.csv',
            maxFiles: 1,
            maxFilesize: 250,
            paramName: 'attachment', // required by restAPI
            parallelUploads: 1,
            autoQueue: true, // Make sure the files aren't queued until manually added
            autoProcessQueue: false, // Not to upload file at once on droping file.
            dictMaxFilesExceeded: 'You can not upload more than 1 file.', // overwrite dict..Option in case that dropzone.js changes it.
            dictFileTooBig: 'You can not upload file larger than 250MB.',
            onError: (errorMessage) => {
                const subscription = this._alertWindow.error('Error', [errorMessage]).subscribe((result: boolean) => {
                    subscription.unsubscribe();
                });
            },
            readOnly: false,
            dropzone: null,
            upload: null,
            onAddedfile: (file, xhr, config: TamUploadConfig) => {
                // check file extension, only CSV is allowed
                const reg = /\.csv$/g;
                if (!reg.test(file.name)) {
                    this._toast.notify({
                        message: 'Only CSV can be uploaded.',
                        style: NotificationStyles.Error
                    });
                    return;
                }

                // enable save button
                this.btnDisabled = false;

                // check file if new added file or existing file
                let acceptedFile = new TamUploadUIFile();
                if (file.upload !== undefined && file.upload.uuid) {
                    acceptedFile.name = file.name;

                    // set progress as 100% and serverName to mark it as finished upload.
                    acceptedFile.progress = '100%';
                    acceptedFile.isCompleted = true;
                    acceptedFile.serverName = file.name;
                } else {
                    // on update file case
                    acceptedFile = file;
                }

                this._dropedFile = file;

                config.files = [];
                // update dropzone UI to show file on it.
                config.files.push(acceptedFile);
            },
            onSending: (file, xhr, config) => {
            },
            onSuccess: (file, responseObject, config) => {

            },
            onRemove: (file, dropzone, inputConfig) => {
                // disable save button
                this.btnDisabled = true;
                dropzone.removeAllFiles(true); // clear dropzone
                inputConfig.files = []; // clear preview zone.

                return false; // Mark the file uploaded, go to 'else' in tam-upload remove.
            },
            onProgress: (file, progress, config) => {
            },
            onStatusChange: (workflow, changedEditing) => {
                this.uploadEditing += changedEditing;
            },
            onNameChange: (filename, oldfilename, config, index) => {
            },
            isDownloadable: false,
            isPreviewable: false
        };

        // show existing file on dropzone as 100% uploaded status.
        this.uploadConfig.files.push(this._parseTamUploadUIFile(selectedRowData.fileName));
        // set save button disabled if no change.
        this.btnDisabled = true;
    }

    private _parseTamUploadUIFile(filename): TamUploadUIFile {
        const tamUploadUIFile = new TamUploadUIFile();
        tamUploadUIFile.name = filename;
        tamUploadUIFile.isNew = false;
        tamUploadUIFile.isCompleted = true;
        // set progress as 100% and serverName to mark it as finished upload.
        tamUploadUIFile.progress = '100%';
        tamUploadUIFile.serverName = filename;
        return tamUploadUIFile;
    }

    /**
     * Parses CSV to data. It's hosted in browser using Papaparse lib.
     * https://www.papaparse.com/docs
     * The return data is in 'complete' callback function, it only can be used
     * in its thread, so we have to use callBackToSendRequest function to access
     * result data and pass service to consume it.
     * @param selectedRowData , the selected row data from flatfilemanagement page.
     * @param file , the dropzone accepted file.
     * @param flatFileDataProviderService , flatFileDataProviderService to upload file.
     * @param flatFileActionService , to update the row in flatfilemanagement list.
     * @param callBackToSendRequest , callback function.
     */
    private _parseCSVData(selectedRowData, localFile, flatFileDataProviderService, flatFileActionService, toastService,
        callBackToSendRequest) {
        Papaparse.parse(localFile, {
            complete: function (results, file) {
                callBackToSendRequest(results, file, selectedRowData, flatFileDataProviderService, flatFileActionService, toastService);
            }
        });
    }

    private _sendRequest(results, file, selectedRowData, flatFileDataProviderService, flatFileActionService, toastService) {
        const flatFileRowSetting = {
            description: '', entityReference: 'SHORTNAME', aliasPrefix: '',
            type: 1, name: '', filename: '', columns: [], previewRows: false
        };
        flatFileRowSetting.name = selectedRowData.name; // the row name
        flatFileRowSetting.filename = file.name; // source: the cvs file name

        flatFileRowSetting.columns = results.data[0].map(function (col) {
            return { name: col, type: 'character varying(255)' };
        });
        flatFileRowSetting.previewRows = results.data.filter(function (currentValue, index) {
            return index > 0 && index < 6;
        });

        flatFileDataProviderService.updateFlatfile(selectedRowData.id, null, file).pipe(
            take(1),
            catchError((e) => {
                toastService.notify({
                    message: e.message,
                    style: NotificationStyles.Error
                });
                return observableOf();
            })).subscribe((res) => {
                // only needs to update filename. on editing, it get data via id.
                selectedRowData.filename = file.name;
                // update the row in flatfilemanagement list.
                flatFileActionService.updateItem$.next(selectedRowData);
                let toastMessage = '';
                if (res['dataErrors']) {
                    let errorColumnNumber = 0;
                    // tslint:disable-next-line: forin
                    for (const key in res['dataErrors']) {
                        toastMessage += 'Column ' + res['dataErrors'][key]['name'] + ', ';
                        errorColumnNumber++;
                    }
                    if (errorColumnNumber === 1) {
                        toastMessage = toastMessage.substring(0, toastMessage.length - 2) + ' has invalid data, please have a check.';
                    } else {
                        toastMessage = toastMessage.substring(0, toastMessage.length - 2) + ' have invalid data, please have a check.';
                    }
                    toastService.notify({
                        message: toastMessage,
                        style: NotificationStyles.Warning
                    });
                } else {
                    toastService.notify({
                        message: 'New data has been imported into Tamale successfully.',
                        style: NotificationStyles.Success
                    });
                }
            });
    }
}
